import produce from 'immer'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

type LineSegment = [ImmutableVector3, ImmutableVector3]

const toVector = (point: Point): ImmutableVector3 => new ImmutableVector3(point.x, point.y, point.z)

const vectorsEqual = (a: ImmutableVector3, b: ImmutableVector3): boolean =>
  a.x === b.x && a.y === b.y && a.z === b.z

export const hasWallFormGeometryChanges = (
  originalPoints: Point[],
  start: ImmutableVector3,
  end: ImmutableVector3,
): boolean => {
  if (originalPoints.length < 2) return false

  const wallStart = toVector(originalPoints[0])
  const wallEnd = toVector(originalPoints[1])

  return !vectorsEqual(start, wallStart) || !vectorsEqual(end, wallEnd)
}

export const buildPreviewWallShapePoints = (
  originalPoints: Point[],
  start: ImmutableVector3,
  end: ImmutableVector3,
): ImmutableVector3[] => {
  if (originalPoints.length < 2) return []

  const points = originalPoints.map(toVector)
  const last = points.length - 1

  points[0] = new ImmutableVector3(start.x, start.y, start.z)
  points[1] = new ImmutableVector3(end.x, end.y, end.z)

  if (last === 2) {
    points[2] = new ImmutableVector3(start.x, start.y, points[2].z)
  } else if (points.length > 2) {
    points[2] = new ImmutableVector3(end.x, end.y, points[2].z)
    points[last] = new ImmutableVector3(start.x, start.y, points[last].z)
  }

  return points
}

export const applyFormValuesToWall = (
  wall: ShapeObject,
  start: ImmutableVector3,
  end: ImmutableVector3,
  placement: WallPlacement,
): ShapeObject => {
  const previewPoints = buildPreviewWallShapePoints(wall.shape.points, start, end)

  return produce(wall, draft => {
    previewPoints.forEach((point, index) => {
      draft.shape.points[index].x = point.x
      draft.shape.points[index].y = point.y
      draft.shape.points[index].z = point.z
    })
    draft.placement = placement
  })
}

export const buildWallShapeOutlineLines = (
  points: ImmutableVector3[],
): {
  outlineLines: LineSegment[]
  startEdgeLine: LineSegment | null
  endEdgeLine: LineSegment | null
} => {
  if (points.length < 2) {
    return { outlineLines: [], startEdgeLine: null, endEdgeLine: null }
  }

  const lastIndex = points.length - 1
  const outlineLines: LineSegment[] = []

  for (let i = 0; i < lastIndex; i++) {
    if (i === 1) continue
    outlineLines.push([points[i], points[i + 1]])
  }

  return {
    outlineLines,
    endEdgeLine: points.length > 2 ? [points[1], points[2]] : null,
    startEdgeLine: points.length > 2 ? [points[0], points[lastIndex]] : null,
  }
}
